/**
 * Tetrish - Tetromino Pieces
 * Defines the different tetromino pieces, their shapes, and movement behavior
 */

// Tetromino types
const TETROMINO_TYPES = {
	I: 'i',
	J: 'j',
	L: 'l',
	O: 'o',
	S: 's',
	T: 't',
	Z: 'z'
};

// Tetromino shapes
const TETROMINOES = {
	i: {
		shapes: [
			[
				[0, 0, 0, 0],
				[1, 1, 1, 1],
				[0, 0, 0, 0],
				[0, 0, 0, 0]
			],
			[
				[0, 0, 1, 0],
				[0, 0, 1, 0],
				[0, 0, 1, 0],
				[0, 0, 1, 0]
			],
			[
				[0, 0, 0, 0],
				[0, 0, 0, 0],
				[1, 1, 1, 1],
				[0, 0, 0, 0]
			],
			[
				[0, 1, 0, 0],
				[0, 1, 0, 0],
				[0, 1, 0, 0],
				[0, 1, 0, 0]
			]
		],
		startOffsetX: 3
	},
	j: {
		shapes: [
			[
				[1, 0, 0],
				[1, 1, 1],
				[0, 0, 0]
			],
			[
				[0, 1, 1],
				[0, 1, 0],
				[0, 1, 0]
			],
			[
				[0, 0, 0],
				[1, 1, 1],
				[0, 0, 1]
			],
			[
				[0, 1, 0],
				[0, 1, 0],
				[1, 1, 0]
			]
		],
		startOffsetX: 3
	},
	l: {
		shapes: [
			[
				[0, 0, 1],
				[1, 1, 1],
				[0, 0, 0]
			],
			[
				[0, 1, 0],
				[0, 1, 0],
				[0, 1, 1]
			],
			[
				[0, 0, 0],
				[1, 1, 1],
				[1, 0, 0]
			],
			[
				[1, 1, 0],
				[0, 1, 0],
				[0, 1, 0]
			]
		],
		startOffsetX: 3
	},
	o: {
		shapes: [
			[
				[1, 1],
				[1, 1]
			]
		],
		startOffsetX: 4
	},
	s: {
		shapes: [
			[
				[0, 1, 1],
				[1, 1, 0],
				[0, 0, 0]
			],
			[
				[0, 1, 0],
				[0, 1, 1],
				[0, 0, 1]
			],
			[
				[0, 0, 0],
				[0, 1, 1],
				[1, 1, 0]
			],
			[
				[1, 0, 0],
				[1, 1, 0],
				[0, 1, 0]
			]
		],
		startOffsetX: 3
	},
	t: {
		shapes: [
			[
				[0, 1, 0],
				[1, 1, 1],
				[0, 0, 0]
			],
			[
				[0, 1, 0],
				[0, 1, 1],
				[0, 1, 0]
			],
			[
				[0, 0, 0],
				[1, 1, 1],
				[0, 1, 0]
			],
			[
				[0, 1, 0],
				[1, 1, 0],
				[0, 1, 0]
			]
		],
		startOffsetX: 3
	},
	z: {
		shapes: [
			[
				[1, 1, 0],
				[0, 1, 1],
				[0, 0, 0]
			],
			[
				[0, 0, 1],
				[0, 1, 1],
				[0, 1, 0]
			],
			[
				[0, 0, 0],
				[1, 1, 0],
				[0, 1, 1]
			],
			[
				[0, 1, 0],
				[1, 1, 0],
				[1, 0, 0]
			]
		],
		startOffsetX: 3
	}
};

// ---------------------------------------------------------------------------
// SRS Wall Kick Data
// Offsets are in screen coordinates: positive x = right, positive y = DOWN.
// (Standard Tetris guideline tables have y-up, so y signs are flipped here.)
// Key format: "fromRotation_toRotation" → array of [dx, dy] to try in order.
// ---------------------------------------------------------------------------

const SRS_KICKS_JLSTZ = {
	'0_1': [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]],
	'1_0': [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]],
	'1_2': [[0, 0], [1, 0], [1, 1], [0, -2], [1, -2]],
	'2_1': [[0, 0], [-1, 0], [-1, -1], [0, 2], [-1, 2]],
	'2_3': [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]],
	'3_2': [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]],
	'3_0': [[0, 0], [-1, 0], [-1, 1], [0, -2], [-1, -2]],
	'0_3': [[0, 0], [1, 0], [1, -1], [0, 2], [1, 2]],
};

const SRS_KICKS_I = {
	'0_1': [[0, 0], [-2, 0], [1, 0], [-2, 1], [1, -2]],
	'1_0': [[0, 0], [2, 0], [-1, 0], [2, -1], [-1, 2]],
	'1_2': [[0, 0], [-1, 0], [2, 0], [-1, -2], [2, 1]],
	'2_1': [[0, 0], [1, 0], [-2, 0], [1, 2], [-2, -1]],
	'2_3': [[0, 0], [2, 0], [-1, 0], [2, -1], [-1, 2]],
	'3_2': [[0, 0], [-2, 0], [1, 0], [-2, 1], [1, -2]],
	'3_0': [[0, 0], [1, 0], [-2, 0], [1, 2], [-2, -1]],
	'0_3': [[0, 0], [-1, 0], [2, 0], [-1, -2], [2, 1]],
};

/**
 * Returns the SRS wall kick offsets for a given piece type and rotation transition.
 * Each offset is [dx, dy] in screen coordinates (y-down).
 */
export function getWallKicks(pieceType, fromRotation, toRotation) {
	if (pieceType === 'o') return [[0, 0]]; // O-piece: no kicks needed
	const key = `${fromRotation}_${toRotation}`;
	if (pieceType === 'i') return SRS_KICKS_I[key] ?? [[0, 0]];
	return SRS_KICKS_JLSTZ[key] ?? [[0, 0]];
}

/**
 * Tetromino class - represents a single tetromino piece
 */
export class Tetromino {
	constructor(type) {
		this.type = type;
		this.rotation = 0;
		this.position = {
			x: TETROMINOES[type].startOffsetX,
			y: 0
		};
	}

	/**
	 * Get the current shape based on rotation
	 */
	getCurrentShape() {
		return TETROMINOES[this.type].shapes[this.rotation];
	}

	/**
	 * Move the piece
	 */
	move(dx, dy = 0) {
		this.position.x += dx;
		this.position.y += dy;
	}

	/**
	 * Rotate the piece clockwise
	 */
	rotate() {
		this.rotation = (this.rotation + 1) % TETROMINOES[this.type].shapes.length;
	}

	/**
	 * Rotate the piece counter-clockwise
	 */
	rotateBack() {
		this.rotation = (this.rotation - 1 + TETROMINOES[this.type].shapes.length) % TETROMINOES[this.type].shapes.length;
	}

	/**
	 * Reset piece to initial position
	 */
	reset() {
		this.position.x = TETROMINOES[this.type].startOffsetX;
		this.position.y = 0;
		this.rotation = 0;
	}

	/**
	 * Create a clone of this piece
	 */
	clone() {
		const clonedPiece = new Tetromino(this.type);
		clonedPiece.rotation = this.rotation;
		clonedPiece.position = { ...this.position };
		return clonedPiece;
	}
}

/**
 * TetrominoFactory - creates random tetromino pieces
 */
export class TetrominoFactory {
	constructor() {
		this.tetrominoTypes = Object.values(TETROMINO_TYPES);
		this.bag = [...this.tetrominoTypes]; // 7-bag randomization system
		this.shuffle(this.bag);
	}

	/**
	 * Get a random piece using 7-bag randomization
	 * (ensures all 7 pieces appear before any repeats)
	 */
	getRandomPiece() {
		// If bag is empty, refill and shuffle
		if (this.bag.length === 0) {
			this.bag = [...this.tetrominoTypes];
			this.shuffle(this.bag);
		}

		// Get and remove the first piece from the bag
		const type = this.bag.pop();
		return new Tetromino(type);
	}

	/**
	 * Shuffle an array (Fisher-Yates algorithm)
	 */
	shuffle(array) {
		for (let i = array.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[array[i], array[j]] = [array[j], array[i]];
		}
		return array;
	}
}
